import React, { useState, useMemo, useEffect } from 'react';
import { THEMES, DENSITY_MODES, applyVariation } from './themes/themes';
import { getCampaignPalettes } from './themes/palettes';
import { buildMockDashboardData } from './mocks/mockData';
import { HomeView } from './components/home/HomeView';
import { CampaignView } from './components/campaign-ornate/CampaignView';
import { CampaignViewV2 } from './components/campaign-modern/CampaignViewV2';
import { ThemePickerModal } from './components/chrome/ThemePickerModal';
import { CampaignSettingsModal } from './components/chrome/CampaignSettingsModal';
import { AnnouncementComposerModal } from './components/chrome/AnnouncementComposerModal';
import { JoinCampaignModal } from './components/chrome/JoinCampaignModal';
import { CreateCharacterModal, buildImportReviewThemeVars } from './components/chrome/CreateCharacterModal';
import CharacterImportReviewModal from '../character-import';
import { DemoCampaignModal } from './components/chrome/DemoCampaignModal';
import { NicknamePromptModal } from './components/chrome/NicknamePromptModal';
import { CampaignForgeV2 } from './components/chrome/forge-v2/CampaignForgeV2';
import { THEME_VARIANTS } from './components/chrome/forge-v2/constants';
import { WorldLoreModal } from './components/chrome/WorldLoreModal';
import { SignInView } from './components/auth/SignInView';
import { ProtoPanel } from './components/dev/ProtoPanel';
import { DEMO_CAMPAIGN_ID, mergeDemoCampaignSet } from '../../demoCampaign';
import { isCampaignTestCharacter } from '../../campaignCharacterTargets';

// PlayerDashboard renders the visual dashboard against a `data` object
// shaped by useDashboardData() (live) or buildMockDashboardData() (mock).
// When `data` is omitted the mock fallback fires — that's the prototype /
// storybook entry point. The live mount wires data + onSelectCampaign /
// onEnterApp / onSignOut from AuthedApp in src/index.js.
// localStorage helpers mirror App.js's theme persistence so changing the
// theme in the dashboard reflects when the user enters App, and vice versa.
// Keys are the same `bmg-*` set App.js uses (App.js line ~122236), plus
// the dashboard-specific `bmg-campaign-style`.
// Three scope tiers for theme persistence:
//   'everything' — global bmg-* keys shared with App.js
//   'dashboard'  — bmg-dashboard-* override keys read only by the dashboard
//   'campaign'   — bmg-campaign-<id>-* override keys read when viewing that campaign
// Read priority (highest wins): campaign override -> dashboard override -> global.
const globalKey = (field) => `bmg-${field === 'themeId' ? 'theme-id'
  : field === 'variationId' ? 'variation-id'
  : field === 'cardStyle' ? 'card-style'
  : field === 'hoverEffects' ? 'hover-effects'
  : field === 'campaignStyle' ? 'campaign-style'
  : field}`;
const dashboardKey = (field) => `bmg-dashboard-${field}`;
const campaignKey = (campaignId, field) => `bmg-campaign-${campaignId}-${field}`;
const showTestCharactersKey = (campaignId) => campaignKey(campaignId, 'show-test-characters');
const CAMPAIGN_THEME_ID_ALIASES = Object.freeze({
  'classic-fantasy': 'fantasy-classic',
  'verdant-grove': 'fantasy-nature',
});

const isCampaignManager = (campaign) => campaign?.role === 'owner' || campaign?.role === 'dm';
const isDashboardTestCharacter = (row) => isCampaignTestCharacter(row);
const allowsPracticeRowsInDashboard = (campaign, campaignId) => (
  campaign?.isDemo === true || campaignId === DEMO_CAMPAIGN_ID
);
const readLS = (key) => {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(key);
  } catch { return null; }
};
const writeLS = (key, value) => {
  try {
    if (typeof window === 'undefined') return;
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch { /* ignore quota / privacy errors */ }
};

// Resolve a field by checking the campaign override (if a campaign id is
// given), then the dashboard override, then the global key, then a default.
const resolveField = (field, campaignId, fallback) => {
  if (campaignId) {
    const v = readLS(campaignKey(campaignId, field));
    if (v != null) return v;
  }
  const dash = readLS(dashboardKey(field));
  if (dash != null) return dash;
  const global = readLS(globalKey(field));
  if (global != null) return global;
  return fallback;
};

const normalizeDashboardThemeId = (themeId) => {
  if (!themeId || typeof themeId !== 'string') return null;
  const id = CAMPAIGN_THEME_ID_ALIASES[themeId] || themeId;
  return THEMES[id] ? id : null;
};

const findForgeThemeVariant = (themeId, variantId, rawThemeId = null) => {
  if (!themeId || !variantId) return null;
  const variantSets = [
    rawThemeId && THEME_VARIANTS[rawThemeId],
    THEME_VARIANTS[themeId],
  ].filter(Boolean);
  for (const variants of variantSets) {
    const match = variants.find((variant) => variant.id === variantId);
    if (match) return match;
  }
  return null;
};

export const PlayerDashboard = ({
  theme: themeProp,
  themeId: themeIdProp,
  variationId: variationIdProp,
  density: densityProp,
  cardStyle: cardStyleProp,
  cardFinish: cardFinishProp = 'solid',
  hoverEffects: hoverEffectsProp,
  bgColor: bgColorProp = '#000000',
  campaignStyle: campaignStyleProp,
  showThemePicker = true,
  showProtoPanel = false,
  initialView = 'home',
  // Live-data props (omit for prototype/storybook mode):
  data: dataProp,
  onSelectCampaign,
  onWorldSummariesNeedChange,
  // Launch handlers: each takes care of its own Convex mutation +
  // onEnterApp(launchTarget) dispatch. Wired by AuthedApp in src/index.js.
  // In prototype/storybook mode these are no-ops.
  onEnterAsCampaignChar,   // (campaignId, charId) => void
  onEnterAsStandaloneChar, // (charId) => void
  onEnterAsCampaignDm,     // (campaignId) => void
  onEnterDemoCampaign,     // ({ role, charId }) => void
  onSaveCampaignSettings,    // (campaignId, patch) => Promise<void>
  onDeleteCampaign,          // (campaignId, confirmName) => Promise<void>; owner-only soft delete
  onRemoveCampaignCharacter, // (campaignId, charId) => Promise<void>; DM/owner roster removal
  onDeleteStandaloneCharacter, // (charId) => Promise<void>; owner-only soft delete
  onUpdateCampaignTestCharacterPresets, // (campaignId, { testPresetCategories }) => Promise<void>
  // Phase C announcement handlers — modal is local state, the parent
  // owns the actual Convex mutations:
  onCreateAnnouncement,      // (campaignId, { title, body, pinned }) => Promise<void>
  onUpdateAnnouncement,      // (announcementId, patch) => Promise<void>
  onDeleteAnnouncement,      // (announcementId) => Promise<void>
  // Polish (2026-05-17) — Party Journal launch + Dev access overlay.
  onOpenPartyJournal,        // (campaignId) => void; launches App into journal view
  onOpenCampaignModule,      // (campaignId, view) => void; launches App into a feature module
  onOpenCampaignSandbox,     // (campaignId) => void; launches local-only sandbox
  onResetCampaignSandbox,    // (campaignId) => void; clears and reseeds local-only sandbox
  onSyncCampaignSandbox,     // (campaignId) => void; replaces local sandbox with current campaign snapshot
  onOpenDevDashboard,        // () => void; opens dev dashboard overlay
  onOpenCreateCharacter,     // () => void; LEGACY fallback — launches the old standalone create screen. Used only when the dashboard-native handlers below are absent.
  // Dashboard-native character creation (2026-07-14). When provided, the
  // "New character" actions open a themed modal here instead of launching
  // the legacy select screen.
  onCreateStandaloneCharacter, // ({ name, race, charClass, level, maxHp }) => Promise<void>; creates + enters the app
  onApplyCharacterImportCreate, // ({ createArgs, patch, warnings }) => Promise<void>; reviewed-import create + enter
  characterImportUploadClient,  // ({ source, mode, includeEquipment, file }) => Promise<payload>; hosted PDF upload
  onOpenCampaignCharacterSetup, // (campaignId) => void; opens create/clone character setup
  // Polish round 3 (2026-05-17) — empty-state CTAs + demo picker.
  onCreateCampaign,          // ({ name, tagline, description, ...features, schedule, generateInvite, dummyChars }) => Promise<{campaignId, inviteCode?}>
  // Phase III / F.6 (2026-05-18) — submit hook for the new CampaignForgeV2
  // wizard. Takes the wizard's `form` shape directly and runs the adapted
  // mutation chain. The legacy onCreateCampaign path remains as a minimal
  // fallback for prototype/storybook mounts that do not provide this hook.
  onCreateCampaignFromForgeV2, // (form) => Promise<{campaignId}>
  onPreviewCampaignForgeSandbox, // (form) => void; local-only sandbox preview, no Convex writes
  onCreateCampaignInvite,    // (campaignId) => Promise<{code}>
  onPreviewInvite,           // (code: string) => Promise<{ valid, reason?, campaignName?, ... }>
  onAcceptInvite,            // (code: string) => Promise<void>; join a campaign by code
  onPromoteLoreSource,        // ({ campaignId, sourceType, sourceId }) => Promise<void>
  onCopyLoreSourceToCampaign, // ({ campaignId, sourceType, sourceId }) => Promise<void>
  // Polish round 4 (2026-05-17) — nickname management.
  onSetNickname,             // (nickname: string) => Promise<void>
  myPreferences,             // { nickname, email, ... } from userPreferences.myPreferences (null until loaded)
  onSignOut,
}) => {
  // Theme state: seed from prop (test override) → resolveField (campaign
  // override > dashboard override > global localStorage > default). Initial
  // mount has no campaign context so only dashboard + global keys are read;
  // a useEffect below re-resolves when the user enters a campaign view.
  const [internalThemeId, setInternalThemeId] = useState(
    () => themeIdProp || resolveField('themeId', null, 'fantasy-dark'),
  );
  const [internalVariationId, setInternalVariationId] = useState(
    () => variationIdProp || resolveField('variationId', null, 'default'),
  );
  const [internalDensity, setInternalDensity] = useState(
    () => densityProp || resolveField('density', null, 'comfortable'),
  );
  const [internalCardStyle, setInternalCardStyle] = useState(
    () => cardStyleProp || resolveField('cardStyle', null, 'glass'),
  );
  const [internalHoverEffects, setInternalHoverEffects] = useState(
    () => (hoverEffectsProp !== undefined
      ? !!hoverEffectsProp
      : resolveField('hoverEffects', null, 'true') !== 'false'),
  );
  const [internalCardFinish] = useState(cardFinishProp);
  // Page background color. The previous toggle was binary (black vs theme
  // bg); now any hex color is allowed via the swatch + native picker in
  // the topbar. The theme background is still available as a quick-pick.
  const [bgColor, setBgColor] = useState(bgColorProp);
  // Campaign page style — 'ornate' (v1: polychromatic glass cards) or
  // 'modern' (v2: solid panels, restrained accents). Set in the theme
  // picker; controls which view the home page routes to when a campaign
  // is clicked.
  const [campaignStyle, setCampaignStyle] = useState(
    () => campaignStyleProp || resolveField('campaignStyle', null, 'ornate'),
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  // Routing state: 'signin' | 'home' | { name: 'campaign', id } | { name: 'campaign-v2', id }
  const [view, setView] = useState(
    initialView === 'signin' ? { name: 'signin' }
    : initialView === 'home' ? { name: 'home' }
    : typeof initialView === 'object' ? initialView
    : { name: 'home' }
  );

  // Whichever campaign we're currently inspecting (if any). Drives the
  // theme-scope re-resolution and the modal's scope context.
  const activeCampaignContextId = (view.name === 'campaign' || view.name === 'campaign-v2')
    ? view.id
    : null;

  // Dashboard data: live (Convex) via prop, mock (prototype) as fallback.
  // The mock object is built once per render via useMemo so reference
  // identity stays stable and downstream useMemo deps don't thrash.
  const fallbackData = useMemo(() => buildMockDashboardData(), []);
  const baseData = dataProp || fallbackData;
  const [testCharacterVisibilityOverrides, setTestCharacterVisibilityOverrides] = useState({});
  const [createdInviteByCampaignId, setCreatedInviteByCampaignId] = useState({});
  // Dashboard-native character creation (2026-07-14).
  const [createCharacterOpen, setCreateCharacterOpen] = useState(false);
  const [importReviewOpen, setImportReviewOpen] = useState(false);
  const data = useMemo(() => {
    const mergedData = mergeDemoCampaignSet(baseData);
    const campaignExtra = { ...(mergedData.campaignExtra || {}) };
    for (const [campaignId, inviteCode] of Object.entries(createdInviteByCampaignId)) {
      if (!inviteCode) continue;
      campaignExtra[campaignId] = {
        ...(campaignExtra[campaignId] || {}),
        invite: inviteCode,
      };
    }
    const visibilityByCampaignId = {};
    for (const campaign of mergedData.campaigns || []) {
      const campaignId = campaign.id || campaign.campaignId;
      const canManage = isCampaignManager(campaign);
      const allowPracticeRows = allowsPracticeRowsInDashboard(campaign, campaignId);
      const hasOverride = allowPracticeRows && Object.prototype.hasOwnProperty.call(
        testCharacterVisibilityOverrides,
        campaignId,
      );
      const raw = canManage && allowPracticeRows && !campaign?.isDemo
        ? (hasOverride
          ? (testCharacterVisibilityOverrides[campaignId] ? 'true' : 'false')
          : readLS(showTestCharactersKey(campaignId)))
        : null;
      visibilityByCampaignId[campaignId] = {
        canManage,
        allowPracticeRows,
        showTestCharacters: allowPracticeRows && (campaign?.isDemo ? true : (canManage ? raw !== 'false' : false)),
        hiddenTestCharacterCount: 0,
        visibleTestCharacterCount: 0,
        totalTestCharacterCount: 0,
      };
    }
    const filteredCharacters = {};
    const hiddenTestIdsByCampaignId = {};
    for (const [campaignId, rows] of Object.entries(mergedData.campaignCharacters || {})) {
      const visibility = visibilityByCampaignId[campaignId] || {
        canManage: false,
        allowPracticeRows: false,
        showTestCharacters: false,
      };
      const showTestCharacters = visibility.showTestCharacters;
      const rowList = Array.isArray(rows) ? rows : [];
      const testRows = rowList.filter(isDashboardTestCharacter);
      const hiddenTestRows = showTestCharacters ? [] : testRows;
      const visibleRows = showTestCharacters
        ? rowList
        : rowList.filter((row) => !isDashboardTestCharacter(row));
      visibilityByCampaignId[campaignId] = {
        canManage: visibility.canManage,
        allowPracticeRows: visibility.allowPracticeRows,
        showTestCharacters,
        hiddenTestCharacterCount: hiddenTestRows.length,
        visibleTestCharacterCount: visibleRows.filter(isDashboardTestCharacter).length,
        totalTestCharacterCount: visibility.allowPracticeRows ? testRows.length : 0,
      };
      filteredCharacters[campaignId] = visibleRows;
      hiddenTestIdsByCampaignId[campaignId] = new Set(hiddenTestRows.map((row) => row.id || row.charId));
    }

    const filteredFriends = {};
    for (const [campaignId, rows] of Object.entries(mergedData.campaignFriends || {})) {
      const showTestCharacters = Boolean(visibilityByCampaignId[campaignId]?.showTestCharacters);
      const hiddenIds = hiddenTestIdsByCampaignId[campaignId] || new Set();
      filteredFriends[campaignId] = (Array.isArray(rows) ? rows : [])
        .filter((row) => showTestCharacters || !hiddenIds.has(row.id || row.charId));
    }

    const campaigns = (mergedData.campaigns || []).map((campaign) => {
      const campaignId = campaign.id || campaign.campaignId;
      const visibility = visibilityByCampaignId[campaignId] || {
        canManage: isCampaignManager(campaign),
        allowPracticeRows: allowsPracticeRowsInDashboard(campaign, campaignId),
        showTestCharacters: allowsPracticeRowsInDashboard(campaign, campaignId) && campaign?.isDemo === true,
        hiddenTestCharacterCount: 0,
        visibleTestCharacterCount: 0,
        totalTestCharacterCount: 0,
      };
      const myCharacterIsHidden = isDashboardTestCharacter(campaign.myCharacter)
        && !visibility.showTestCharacters;
      const myCharacter = myCharacterIsHidden ? null : campaign.myCharacter;
      const hiddenId = myCharacterIsHidden
        ? (campaign.myCharacter?.id || campaign.myCharacter?.charId || null)
        : null;
      const party = myCharacterIsHidden
        ? (campaign.party || []).filter((entry) => {
          if (entry && typeof entry === 'object') return (entry.id || entry.charId) !== hiddenId;
          return false;
        })
        : campaign.party;
      return {
        ...campaign,
        myCharacter,
        party,
        yourChar: myCharacterIsHidden ? null : campaign.yourChar,
        yourCharId: myCharacterIsHidden ? null : campaign.yourCharId,
        myCharacterId: myCharacterIsHidden ? null : campaign.myCharacterId,
        showTestCharacters: visibility.showTestCharacters,
        hiddenTestCharacterCount: visibility.hiddenTestCharacterCount,
        visibleTestCharacterCount: visibility.visibleTestCharacterCount,
        totalTestCharacterCount: visibility.totalTestCharacterCount,
      };
    });

    const activeCharacter = mergedData.activeCharacter
      && isDashboardTestCharacter(mergedData.activeCharacter)
      && !visibilityByCampaignId[mergedData.activeCharacter.campaignId]?.showTestCharacters
      ? null
      : mergedData.activeCharacter;

    return {
      ...mergedData,
      activeCharacter,
      campaigns,
      campaignExtra,
      campaignCharacters: filteredCharacters,
      campaignFriends: filteredFriends,
    };
  }, [baseData, testCharacterVisibilityOverrides, createdInviteByCampaignId]);
  const activeCampaignForTheme = useMemo(
    () => activeCampaignContextId
      ? (data?.campaigns || []).find((cp) => (cp.id || cp.campaignId) === activeCampaignContextId) || null
      : null,
    [activeCampaignContextId, data?.campaigns],
  );
  const activeCampaignThemeId = normalizeDashboardThemeId(activeCampaignForTheme?.baseThemeId);
  const activeCampaignThemeRawId = activeCampaignForTheme?.baseThemeId || null;
  const activeCampaignVariantId = activeCampaignForTheme?.baseVariant || null;
  const hasCampaignVariationOverride = !!(
    activeCampaignContextId && readLS(campaignKey(activeCampaignContextId, 'variationId')) != null
  );
  const campaignForgeVariant = (!themeProp && activeCampaignContextId && activeCampaignThemeId === internalThemeId && !hasCampaignVariationOverride)
    ? findForgeThemeVariant(activeCampaignThemeId, activeCampaignVariantId, activeCampaignThemeRawId)
    : null;

  // When the user enters / leaves a campaign view, re-resolve theme fields
  // so campaign-specific overrides and persisted campaign defaults take
  // effect. Priority: explicit campaign localStorage override, then the
  // campaign's Forge-selected theme, then dashboard/global defaults.
  useEffect(() => {
    setInternalThemeId((prev) => {
      if (!activeCampaignContextId) return resolveField('themeId', null, prev) || prev;
      const campaignOverride = readLS(campaignKey(activeCampaignContextId, 'themeId'));
      if (campaignOverride) return normalizeDashboardThemeId(campaignOverride) || prev;
      return activeCampaignThemeId || resolveField('themeId', null, prev) || prev;
    });
    setInternalVariationId((prev) => {
      if (!activeCampaignContextId) return resolveField('variationId', null, prev) || prev;
      const campaignOverride = readLS(campaignKey(activeCampaignContextId, 'variationId'));
      if (campaignOverride) return campaignOverride;
      return resolveField('variationId', null, prev) || prev;
    });
    setInternalDensity((prev) => resolveField('density', activeCampaignContextId, prev) || prev);
    setInternalCardStyle((prev) => resolveField('cardStyle', activeCampaignContextId, prev) || prev);
    setInternalHoverEffects((prev) => {
      const raw = resolveField('hoverEffects', activeCampaignContextId, prev ? 'true' : 'false');
      return raw !== 'false';
    });
    setCampaignStyle((prev) => resolveField('campaignStyle', activeCampaignContextId, prev) || prev);
  }, [activeCampaignContextId, activeCampaignThemeId]);
  const forgeWorlds = data?.worlds || [];
  const forgeWorldsLoading = !!data?.worldsLoading;
  const canImportExistingCampaignSource = !!(
    myPreferences?.devAccessRole === 'betaAdmin'
    || myPreferences?.isDev === true
  );
  const forgeExistingCampaigns = useMemo(() => (
    (data?.campaigns || [])
      .filter((cp) => !cp.isDemo && (cp.role === 'owner' || cp.role === 'dm'))
      .map((cp) => ({
        id: cp.id || cp.campaignId,
        worldId: cp.worldEntityId || cp.worldId || null,
        name: cp.name,
        tagline: cp.tagline || cp.blurb || '',
        dm: cp.dm || '',
        players: cp.players || 0,
        homebrew: cp.homebrew ?? null,
        enabledFeatures: cp.enabledFeatures || {},
      }))
  ), [data?.campaigns]);

  // When entering a campaign view, notify the parent so it can adjust
  // its useDashboardData() selectedCampaignId argument. No-op in mock
  // mode. Always tracks the most-recently-entered campaign id so leaving
  // the campaign view clears the parent's tracker.
  const enterCampaign = (id) => {
    setView({ name: campaignStyle === 'modern' ? 'campaign-v2' : 'campaign', id });
    if (onSelectCampaign) onSelectCampaign(id === DEMO_CAMPAIGN_ID ? null : id);
  };
  const leaveCampaign = () => {
    setView({ name: 'home' });
    if (onSelectCampaign) onSelectCampaign(null);
  };

  const theme = useMemo(() => {
    const PALATINO = '"Palatino Linotype", "Book Antiqua", Palatino, Georgia, serif';
    let base;
    if (themeProp) {
      base = themeProp;
    } else {
      const baseTheme = THEMES[internalThemeId] || THEMES['fantasy-classic'];
      base = applyVariation(baseTheme, internalVariationId);
      if (campaignForgeVariant) {
        base = {
          ...base,
          colors: {
            ...base.colors,
            accent: campaignForgeVariant.accent,
            accentLight: campaignForgeVariant.accentLight,
          },
        };
      }
    }
    // Override typography to Palatino Linotype across all themes — the
    // dashboard establishes its own typographic voice independent of the
    // theme. This keeps the dashboard recognizable; themes affect
    // color/effects only.
    return {
      ...base,
      typography: {
        ...base.typography,
        fontFamily: PALATINO,
        headingFamily: PALATINO,
      },
    };
  }, [themeProp, internalThemeId, internalVariationId, campaignForgeVariant]);

  // Dashboard always renders at the prototype's comfortable density. The
  // density picker still writes to localStorage so App.js (character sheet)
  // can consume it, but the dashboard intentionally ignores it — its spacing
  // is a designed surface, not a user-tunable layout.
  const density = DENSITY_MODES['comfortable'];
  const palettes = useMemo(() => getCampaignPalettes(theme), [theme]);
  const c = theme.colors;
  const t = theme.typography || {};
  const e = theme.effects || {};

  const cardStyle = internalCardStyle;
  const cardFinish = internalCardFinish;

  const containerStyle = {
    minHeight: '100vh',
    background: bgColor,
    fontFamily: t.fontFamily || 'Georgia, serif',
    color: c.textPrimary,
    padding: density.spacing.cardPadding,
    boxSizing: 'border-box',
    fontSize: density.typography.baseFontSize,
  };

  // Shared shell props
  const shellProps = {
    theme, density, palettes,
    cardStyle, cardFinish,
    bgColor, setBgColor, themeBgColor: c.bgPrimary,
    onOpenThemes: () => setPickerOpen(true),
  };

  // ID of the campaign currently open in the Campaign Settings modal.
  // null = no modal. Decoupled from view state so the modal can open
  // while the user is on the home or campaign view alike.
  const [settingsCampaignId, setSettingsCampaignId] = useState(null);

  // Announcement composer state. `{ campaignId, note }` opens the modal;
  // `note=null` means create mode, `note=existing` means edit mode.
  const [composerState, setComposerState] = useState(null);

  // Polish round 3 — empty-state CTA + demo picker modals.
  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [joinCampaignOpen, setJoinCampaignOpen] = useState(false);
  const [demoPickerOpen, setDemoPickerOpen] = useState(false);
  const [worldLoreCampaignId, setWorldLoreCampaignId] = useState(null);
  const [campaignCreationWarning, setCampaignCreationWarning] = useState(null);
  useEffect(() => {
    if (onWorldSummariesNeedChange) onWorldSummariesNeedChange(createCampaignOpen);
  }, [createCampaignOpen, onWorldSummariesNeedChange]);
  // Polish round 4 — nickname prompt. Opens automatically the first time
  // an authenticated user lands on the dashboard with no nickname set;
  // user can dismiss with "Skip" and reopen via the avatar dropdown.
  const [nicknameModalOpen, setNicknameModalOpen] = useState(false);
  const [nicknamePromptedThisSession, setNicknamePromptedThisSession] = useState(false);
  useEffect(() => {
    if (!myPreferences) return; // still loading
    if (nicknamePromptedThisSession) return;
    if (!myPreferences.nickname) {
      setNicknameModalOpen(true);
      setNicknamePromptedThisSession(true);
    }
  }, [myPreferences, nicknamePromptedThisSession]);

  // Action callbacks bundled into one object so subcomponents can pull
  // the handler they need without long prop chains. Each defaults to a
  // no-op in prototype mode.
  const noop = () => {};
  const isDemoCampaignId = (campaignId) => campaignId === DEMO_CAMPAIGN_ID;
  const setShowTestCharactersForCampaign = (campaignId, visible) => {
    if (!campaignId || isDemoCampaignId(campaignId)) return;
    writeLS(showTestCharactersKey(campaignId), visible ? 'true' : 'false');
    setTestCharacterVisibilityOverrides((prev) => ({
      ...prev,
      [campaignId]: !!visible,
    }));
  };
  const actions = {
    onEnterAsCampaignChar: (campaignId, charId) => {
      if (isDemoCampaignId(campaignId)) {
        (onEnterDemoCampaign || noop)({ role: 'player', charId });
        return;
      }
      (onEnterAsCampaignChar || noop)(campaignId, charId);
    },
    onEnterAsStandaloneChar: onEnterAsStandaloneChar || noop,
    onEnterAsCampaignDm: (campaignId) => {
      if (isDemoCampaignId(campaignId)) {
        (onEnterDemoCampaign || noop)({ role: 'dm' });
        return;
      }
      (onEnterAsCampaignDm || noop)(campaignId);
    },
    onEnterDemoCampaign: onEnterDemoCampaign || noop,
    onOpenCampaignSettings: (campaignId) => {
      if (!isDemoCampaignId(campaignId)) setSettingsCampaignId(campaignId);
    },
    onOpenAnnouncementComposer: (campaignId, note) => {
      if (!isDemoCampaignId(campaignId)) setComposerState({ campaignId, note: note || null });
    },
    onOpenPartyJournal: (campaignId) => {
      if (!isDemoCampaignId(campaignId)) (onOpenPartyJournal || noop)(campaignId);
    },
    onOpenCampaignModule: (campaignId, viewName) => {
      if (!isDemoCampaignId(campaignId)) (onOpenCampaignModule || noop)(campaignId, viewName);
    },
    onOpenCampaignSandbox: (campaignId) => {
      if (!isDemoCampaignId(campaignId)) (onOpenCampaignSandbox || noop)(campaignId);
    },
    onResetCampaignSandbox: (campaignId) => {
      if (isDemoCampaignId(campaignId)) return;
      if (typeof window !== 'undefined') {
        const confirmed = window.confirm('Reset this local sandbox? Your live campaign data will not change.');
        if (!confirmed) return;
      }
      (onResetCampaignSandbox || noop)(campaignId);
    },
    onSyncCampaignSandbox: (campaignId) => {
      if (!isDemoCampaignId(campaignId)) (onSyncCampaignSandbox || noop)(campaignId);
    },
    onOpenCampaignCharacterSetup: (campaignId) => {
      if (!isDemoCampaignId(campaignId)) (onOpenCampaignCharacterSetup || noop)(campaignId);
    },
    onRemoveCampaignCharacter: onRemoveCampaignCharacter
      ? async (campaignId, charId, characterName) => {
          if (isDemoCampaignId(campaignId) || !charId) return;
          if (typeof window !== 'undefined') {
            const label = characterName ? `"${characterName}"` : 'this character';
            const confirmed = window.confirm(`Remove ${label} from this campaign?`);
            if (!confirmed) return;
          }
          await onRemoveCampaignCharacter(campaignId, charId);
        }
      : null,
    onDeleteStandaloneCharacter: onDeleteStandaloneCharacter
      ? async (charId, characterName) => {
          if (!charId) return;
          if (typeof window !== 'undefined') {
            const label = characterName ? `"${characterName}"` : 'this solo character';
            const confirmed = window.confirm(`Delete ${label}? This removes it from your solo characters.`);
            if (!confirmed) return;
          }
          await onDeleteStandaloneCharacter(charId);
        }
      : null,
    onCreateCampaignInvite: async (campaignId) => {
      if (isDemoCampaignId(campaignId) || !onCreateCampaignInvite) return null;
      const result = await onCreateCampaignInvite(campaignId);
      const code = result?.code || result?.inviteCode || null;
      if (code) {
        setCreatedInviteByCampaignId((prev) => ({
          ...prev,
          [campaignId]: code,
        }));
      }
      return result;
    },
    onOpenWorldLore: (campaignId) => {
      if (!isDemoCampaignId(campaignId)) setWorldLoreCampaignId(campaignId);
    },
    onSetShowTestCharacters: setShowTestCharactersForCampaign,
    onOpenDevDashboard: onOpenDevDashboard || noop,
    // Prefer the dashboard-native modal; fall back to the legacy standalone
    // launch when the container doesn't provide the new handlers.
    onOpenCreateCharacter: onCreateStandaloneCharacter
      ? () => setCreateCharacterOpen(true)
      : (onOpenCreateCharacter || noop),
    onOpenCreateCampaign: () => setCreateCampaignOpen(true),
    onOpenJoinCampaign: () => setJoinCampaignOpen(true),
    onOpenDemoPicker: () => setDemoPickerOpen(true),
    onOpenDemoCampaignSet: () => enterCampaign(DEMO_CAMPAIGN_ID),
    onSignOut: onSignOut || noop,
  };

  let viewContent;
  if (view.name === 'signin') {
    viewContent = (
      <SignInView
        {...shellProps}
        onSignIn={() => setView({ name: 'home' })}
        onEnterDemoCampaign={actions.onEnterDemoCampaign}
      />
    );
  } else if (view.name === 'campaign') {
    viewContent = (
      <CampaignView
        {...shellProps}
        data={data}
        actions={actions}
        campaignId={view.id}
        onBack={leaveCampaign}
      />
    );
  } else if (view.name === 'campaign-v2') {
    viewContent = (
      <CampaignViewV2
        {...shellProps}
        data={data}
        actions={actions}
        campaignId={view.id}
        onBack={leaveCampaign}
      />
    );
  } else {
    viewContent = (
      <HomeView
        {...shellProps}
        data={data}
        actions={actions}
        onEnterCampaign={enterCampaign}
      />
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {viewContent}

        <button
          onClick={() => setPickerOpen(true)}
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            width: 40, height: 40,
            borderRadius: '50%',
            background: c.bgCardSolid,
            border: `1px solid ${c.borderPrimary}`,
            color: c.textMuted,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: e.cardShadow,
            fontSize: '1rem',
          }}
          title="Theme settings"
        >⚙</button>

      </div>

      {showProtoPanel && (
        <ProtoPanel theme={theme} view={view} onChangeView={setView} />
      )}

      {showThemePicker && pickerOpen && (
        <ThemePickerModal
          theme={theme}
          themeId={themeProp ? null : internalThemeId}
          variationId={themeProp ? null : internalVariationId}
          density={internalDensity}
          cardStyle={cardStyle}
          hoverEffects={internalHoverEffects}
          campaignStyle={campaignStyle}
          scopeContext={activeCampaignContextId
            ? {
                kind: 'campaign',
                campaignId: activeCampaignContextId,
                campaignName: (data?.campaigns || []).find((cp) => cp.id === activeCampaignContextId)?.name,
              }
            : 'global'}
          onApply={(next) => {
            // scope: 'everything' | 'dashboard' | 'campaign'.
            // 'everything' writes the global bmg-* keys App.js reads from;
            // 'dashboard' writes bmg-dashboard-<field> overrides;
            // 'campaign' writes bmg-campaign-<id>-<field> overrides.
            const targetCampaignId = next.scope === 'campaign' ? (next.campaignId || activeCampaignContextId) : null;
            const writeForField = (field, value) => {
              if (next.scope === 'campaign' && targetCampaignId) {
                writeLS(campaignKey(targetCampaignId, field), value);
              } else if (next.scope === 'dashboard') {
                writeLS(dashboardKey(field), value);
              } else {
                writeLS(globalKey(field), value);
              }
            };
            if (next.themeId) {
              setInternalThemeId(next.themeId);
              writeForField('themeId', next.themeId);
            }
            if (next.variationId) {
              setInternalVariationId(next.variationId);
              writeForField('variationId', next.variationId);
            }
            if (next.density) {
              setInternalDensity(next.density);
              writeForField('density', next.density);
            }
            if (next.cardStyle) {
              setInternalCardStyle(next.cardStyle);
              writeForField('cardStyle', next.cardStyle);
            }
            if (typeof next.hoverEffects === 'boolean') {
              setInternalHoverEffects(next.hoverEffects);
              writeForField('hoverEffects', next.hoverEffects ? 'true' : 'false');
            }
            if (next.campaignStyle) {
              setCampaignStyle(next.campaignStyle);
              writeForField('campaignStyle', next.campaignStyle);
            }
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {settingsCampaignId && (() => {
        const targetCampaign = (data?.campaigns || []).find((cp) => cp.id === settingsCampaignId);
        const isOwner = targetCampaign?.role === 'owner';
        return (
          <CampaignSettingsModal
            theme={theme}
            campaign={targetCampaign}
            extra={data?.campaignExtra?.[settingsCampaignId]}
            testCharacters={[]}
            isOwner={isOwner}
            onClose={() => setSettingsCampaignId(null)}
            onSave={onSaveCampaignSettings
              ? (patch) => onSaveCampaignSettings(settingsCampaignId, patch)
              : null}
            onUpdateTestCharacterPresets={null}
            onDelete={onDeleteCampaign && isOwner
              ? async (campaignId, confirmName) => {
                  await onDeleteCampaign(campaignId, confirmName);
                  // If the deleted campaign was the one currently being
                  // viewed, the campaign view will render null on next
                  // tick (the row disappears from data.campaigns) — route
                  // home explicitly to avoid a blank screen.
                  if ((view.name === 'campaign' || view.name === 'campaign-v2') && view.id === campaignId) {
                    setView({ name: 'home' });
                    if (onSelectCampaign) onSelectCampaign(null);
                  }
                  setSettingsCampaignId(null);
                }
              : null}
          />
        );
      })()}

      {createCampaignOpen && (
        <CampaignForgeV2
          theme={theme}
          worlds={forgeWorlds}
          worldsLoading={forgeWorldsLoading}
          existingCampaigns={forgeExistingCampaigns}
          sourceImportEnabled={canImportExistingCampaignSource}
          onCancel={() => setCreateCampaignOpen(false)}
          onPreviewSandbox={onPreviewCampaignForgeSandbox
            ? (payload) => {
                setCreateCampaignOpen(false);
                onPreviewCampaignForgeSandbox(payload);
              }
            : undefined}
          onCreate={async (payload) => {
            let result = null;
            if (!onCreateCampaignFromForgeV2) {
              throw new Error(
                onCreateCampaign
                  ? 'Campaign Forge V2 creation handler is not available; refusing to create a name-only campaign'
                  : 'Campaign Forge V2 creation handler is not available',
              );
            }
            setCampaignCreationWarning(null);
            try {
              result = await onCreateCampaignFromForgeV2(payload);
            } catch (err) {
              if (err?.partialSuccess && err?.campaignId) {
                setCampaignCreationWarning(err.message || 'Campaign created, but some details did not save.');
                if (err.inviteCode) {
                  setCreatedInviteByCampaignId((prev) => ({
                    ...prev,
                    [err.campaignId]: err.inviteCode,
                  }));
                }
                setCreateCampaignOpen(false);
                enterCampaign(err.campaignId);
                return;
              }
              throw err;
            }
            setCreateCampaignOpen(false);
            if (result?.campaignId) {
              if (result.inviteCode) {
                setCreatedInviteByCampaignId((prev) => ({
                  ...prev,
                  [result.campaignId]: result.inviteCode,
                }));
              }
              enterCampaign(result.campaignId);
            }
          }}
        />
      )}

      {campaignCreationWarning && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            right: 18,
            bottom: 18,
            zIndex: 260,
            maxWidth: 420,
            background: c.bgCardSolid || 'rgba(20,20,20,0.96)',
            border: `1px solid ${c.warning || c.accent}`,
            borderRadius: 8,
            padding: '12px 14px',
            color: c.textPrimary,
            fontFamily: t.fontFamily,
            fontSize: '0.86rem',
            boxShadow: e.shadowLg,
            whiteSpace: 'pre-line',
          }}
        >
          <button
            type="button"
            aria-label="Dismiss campaign creation warning"
            onClick={() => setCampaignCreationWarning(null)}
            style={{
              float: 'right',
              marginLeft: 10,
              background: 'transparent',
              border: 'none',
              color: c.textSecondary,
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            x
          </button>
          {campaignCreationWarning}
        </div>
      )}

      {joinCampaignOpen && (
        <JoinCampaignModal
          theme={theme}
          onClose={() => setJoinCampaignOpen(false)}
          onPreview={onPreviewInvite}
          onAccept={async (code) => {
            if (!onAcceptInvite) return null;
            const result = await onAcceptInvite(code);
            if (result?.campaignId) enterCampaign(result.campaignId);
            return result;
          }}
        />
      )}

      {createCharacterOpen && (
        <CreateCharacterModal
          theme={theme}
          onClose={() => setCreateCharacterOpen(false)}
          onCreate={onCreateStandaloneCharacter}
          onOpenImport={onApplyCharacterImportCreate
            ? () => {
                setCreateCharacterOpen(false);
                setImportReviewOpen(true);
              }
            : null}
        />
      )}

      {importReviewOpen && (
        <CharacterImportReviewModal
          mode="create"
          availableSources={['shenyi', 'vental']}
          uploadAppPayload={characterImportUploadClient}
          themeVars={buildImportReviewThemeVars(theme)}
          onClose={() => setImportReviewOpen(false)}
          onApplyCreate={onApplyCharacterImportCreate}
        />
      )}

      {nicknameModalOpen && (
        <NicknamePromptModal
          theme={theme}
          currentNickname={myPreferences?.nickname || ''}
          suggestedNickname={data?.user?.firstName || data?.user?.name || ''}
          isFirstTime={!myPreferences?.nickname}
          onSave={async (nick) => {
            if (onSetNickname) await onSetNickname(nick);
          }}
          onSkip={async () => { /* no-op; user can set later via avatar dropdown */ }}
          onClose={() => setNicknameModalOpen(false)}
        />
      )}

      {demoPickerOpen && (
          <DemoCampaignModal
            theme={theme}
            onClose={() => setDemoPickerOpen(false)}
            onEnter={({ role, charId }) => {
              setDemoPickerOpen(false);
              if (role === 'campaign') {
                enterCampaign(DEMO_CAMPAIGN_ID);
                return;
              }
              if (onEnterDemoCampaign) onEnterDemoCampaign({ role, charId });
            }}
          />
      )}

      {worldLoreCampaignId && (
        <WorldLoreModal
          theme={theme}
          campaign={(data?.campaigns || []).find((cp) => cp.id === worldLoreCampaignId)}
          lore={data?.campaignLoreByCampaign?.[worldLoreCampaignId]}
          migrationPreview={data?.loreMigrationPreviewByCampaign?.[worldLoreCampaignId]}
          loading={!!data?.campaignLoreLoading}
          previewLoading={!!data?.loreMigrationPreviewLoading}
          onPromoteSource={onPromoteLoreSource}
          onCopySourceToCampaign={onCopyLoreSourceToCampaign}
          onClose={() => setWorldLoreCampaignId(null)}
        />
      )}

      {composerState && (
        <AnnouncementComposerModal
          theme={theme}
          campaign={(data?.campaigns || []).find((cp) => cp.id === composerState.campaignId)}
          note={composerState.note}
          onClose={() => setComposerState(null)}
          onSubmit={async (input) => {
            if (input.announcementId && onUpdateAnnouncement) {
              await onUpdateAnnouncement(input.announcementId, {
                title: input.title,
                body: input.body,
                pinned: input.pinned,
              });
            } else if (onCreateAnnouncement) {
              await onCreateAnnouncement(composerState.campaignId, {
                title: input.title,
                body: input.body,
                pinned: input.pinned,
              });
            }
          }}
          onDelete={onDeleteAnnouncement && composerState.note
            ? (announcementId) => onDeleteAnnouncement(announcementId)
            : null}
        />
      )}
    </div>
  );
};


export default PlayerDashboard;
